import classnames from 'classnames';
import { useState, useRef, useEffect } from 'react';
import { Button } from '@acb_devel/design-system/Button';
import { Heading } from '@acb_devel/design-system/Heading';
import { Text } from '@acb_devel/design-system/Text';
import {
  AngleUpIcon,
  AngleDownIcon,
  EditIcon,
  TrashIcon,
} from '@acb_devel/design-system/Icons';
import { useUserContext } from '@contexts/UserContext';

import type { Evaluation } from '@services/evaluations';
import { RegistrationStatus } from '@services/registrations';

import styles from './FeedbackEvaluation.module.scss';

export function FeedbackEvaluation({
  evaluation,
  onEdit,
  onDelete,
}: {
  evaluation: Evaluation;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { user: loggedUser } = useUserContext();

  const [isTruncated, setIsTruncated] = useState(true);
  const [isShortText, setIsShortText] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  const checkTruncation = () => {
    if (textRef.current) {
      const { scrollHeight, offsetHeight } = textRef.current;
      setIsShortText(scrollHeight === offsetHeight);
    }
  };

  useEffect(() => {
    checkTruncation();
    window.addEventListener('resize', checkTruncation);

    return () => {
      window.removeEventListener('resize', checkTruncation);
    };
  }, []);

  return (
    <div className={styles.feedbackEvaluation}>
      <div className={styles.feedbackEvaluation__firstColumn}>
        {evaluation.createdAt && (
          <Text
            as="p"
            variant="body-2"
            className={styles.feedbackEvaluation__title}
          >
            {new Date(evaluation.createdAt).toLocaleDateString()} ·{' '}
            {new Date(evaluation.createdAt)
              .getHours()
              .toString()
              .padStart(2, '0')}
            :
            {new Date(evaluation.createdAt)
              .getMinutes()
              .toString()
              .padStart(2, '0')}
          </Text>
        )}
        {evaluation.givenBy && (
          <Text
            variant="body-2"
            className={styles.feedbackEvaluation__subtitle}
          >
            Añadido por <b>{evaluation.givenBy.emailAddress}</b>
          </Text>
        )}
      </div>
      <div className={styles.feedbackEvaluation__secondColumn}>
        <Heading
          as="p"
          variant="heading-2"
          className={styles.feedbackEvaluation__title}
        >
          {evaluation.registration.event.fullName}
        </Heading>
        <Text
          ref={textRef}
          variant="body-2"
          className={classnames(
            styles.feedbackEvaluation__feedback,
            isTruncated ? styles['feedbackEvaluation__feedback--truncated'] : ''
          )}
        >
          {evaluation.name}
        </Text>
        <div className={styles.feedbackEvaluation__options}>
          {isTruncated && !isShortText && (
            <Button
              variant="flat"
              color="secondary"
              size="md"
              upperCase={false}
              onClick={() => setIsTruncated(!isTruncated)}
            >
              <AngleDownIcon /> Ver feedback completo
            </Button>
          )}
          {!isTruncated && !isShortText ? (
            <Button
              variant="flat"
              color="secondary"
              size="md"
              upperCase={false}
              onClick={() => setIsTruncated(!isTruncated)}
            >
              <AngleUpIcon /> Mostrar menos
            </Button>
          ) : (
            <span />
          )}
          <div className={styles.feedbackEvaluation__buttons}>
            {evaluation?.givenBy?.id === loggedUser?.id &&
              evaluation.registration.status ===
                RegistrationStatus.CONFIRMED && (
                <>
                  <Button
                    color="secondary"
                    size="md"
                    variant="flat"
                    onClick={onEdit}
                  >
                    <EditIcon /> Editar
                  </Button>
                  <Button
                    color="secondary"
                    size="md"
                    variant="flat"
                    onClick={onDelete}
                  >
                    <TrashIcon /> Eliminar
                  </Button>
                </>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
